/* Reports & Financials — a faithful React port of the prototype's rReports screen.
 *
 * The student's quarterly financial pack, all read-only over resolved history:
 * an income statement (last quarter / YTD / last year), a balanced-scorecard
 * radar, a balance sheet (Assets = Liabilities + Equity, with a quarter/year
 * comparison), a curated KPI table (margins, ROS/ROE/EPS, growth, turns,
 * leverage, share, satisfaction) with quarter-over-quarter trends, the stock
 * performance panel, per-product performance, units & revenue by region, and
 * revenue/profit + scorecard trend charts. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { Engine, applyGameCfg, SEGMENTS, REGIONS, COUNTRIES, fmtM, fmtK, fmt$ } from '@venturewrist/engine';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { useReduceMotion } from '../../hooks/useReduceMotion';
import { Flag } from '../../components/Flag';
import type { ScreenProps } from './types';
import '../../styles/reports.css';

const Eng = Engine as any;
const SEGS = SEGMENTS as any[];
const REGS = REGIONS as any[];
const CTRYS = COUNTRIES as any[];
const TT = { background: 'var(--panel)', border: '1px solid var(--card-b)', borderRadius: 10, color: 'var(--txt)' } as const;

function SegTag({ segId }: { segId: string }) {
  const s = SEGS.find((x) => x.id === segId);
  if (!s) return <span>{segId}</span>;
  return <span className="seg-tag" style={{ background: s.color + '22', color: s.color }}>{s.icon} {s.name}</span>;
}
function GenChip({ status, label }: { status: string; label: string }) {
  const S = (Eng.STATUS as any)[status] || (Eng.STATUS as any).current;
  return <span className="gen-chip" style={{ color: S.color }}>{S.icon} {label}</span>;
}
const Trend = ({ cur, prev }: { cur: number | null; prev: number | null }) => {
  if (cur == null || prev == null || cur === prev) return <span className="sub">—</span>;
  const up = cur > prev;
  return <span style={{ color: up ? 'var(--good)' : 'var(--bad)' }}>{up ? '▲' : '▼'}</span>;
};

/* KPI ratios for history index i (mirrors the prototype's kpiStats). */
function kpiStats(g: any, i: number): any {
  const H = g.history, r = H[i]; if (!r) return null;
  const inv = (r.products || []).reduce((a: number, p: any) => a + (p.inventory || 0) * (p.uc || 0), 0);
  const equity = Eng.bookEquity(g, r);
  const ttm = Eng.earnTTM(H, i);
  const op = r.revenue - r.cogs - r.marketing - r.rndSpend - r.freight - (r.storeOps || 0) - r.holding - r.overhead;
  const pq = i > 0 ? H[i - 1] : null, yq = i >= 4 ? H[i - 4] : null;
  return {
    gross: r.revenue > 0 ? (r.revenue - r.cogs) / r.revenue : null,
    op: r.revenue > 0 ? op / r.revenue : null,
    net: r.revenue > 0 ? r.profit / r.revenue : null,
    roe: equity > 0 ? ttm / equity : null,
    eps: ttm / (g.shares || 2e6),
    revG: pq && pq.revenue > 0 ? r.revenue / pq.revenue - 1 : null,
    revGY: yq && yq.revenue > 0 ? r.revenue / yq.revenue - 1 : null,
    asp: r.unitsSold > 0 ? r.revenue / r.unitsSold : null,
    ucost: r.unitsSold > 0 ? r.cogs / r.unitsSold : null,
    contrib: r.revenue > 0 ? (r.revenue - r.cogs - r.freight) / r.revenue : null,
    turns: inv > 0 ? r.cogs * 4 / inv : null,
    mktPct: r.revenue > 0 ? r.marketing / r.revenue : null,
    rndPct: r.revenue > 0 ? r.rndSpend / r.revenue : null,
    stockout: r.stockoutRate,
    cash: r.cash, debt: r.loan, equity,
    d2e: equity > 0 ? r.loan / equity : null,
    share: r.share, brand: r.brand, sat: r.satisfaction,
  };
}

const SCL: Record<string, string> = { profitability: 'Profitability', share: 'Market share', brand: 'Brand', quality: 'Quality', satisfaction: 'Satisfaction', operations: 'Operations', innovation: 'Innovation', finance: 'Fin. health' };

export function Reports({ detail }: ScreenProps) {
  const reduceMotion = useReduceMotion();
  const [isMode, setIsMode] = useState<'q' | 'ytd' | 'ly'>('q');
  const [bsMode, setBsMode] = useState<'q' | 'y'>('q');

  const g = useMemo(() => {
    const wg = structuredClone(detail.state) as any;
    Eng.ensureStock(wg); Eng.ensureFinance(wg); applyGameCfg(wg);
    return wg;
  }, [detail.state]);

  const H = (g.history ?? []) as any[];
  const last = H[H.length - 1];
  const active = g.countries && g.countries.length ? CTRYS.filter((c) => g.countries.includes(c.id)) : CTRYS;

  if (!last) {
    return (
      <div className="rp-root">
        <div className="empty-state"><div className="ic">📊</div>Reports appear after your first quarter.</div>
      </div>
    );
  }

  // ── income statement ───────────────────────────────────────────────────────────
  const incomeSection = () => {
    const yearOf = (r: any) => Math.floor((r.round - 1) / 4), qiOf = (r: any) => (r.round - 1) % 4;
    const curYear = yearOf(last), curQi = qiOf(last);
    const agg = (recs: any[]): any => {
      if (!recs || !recs.length) return null;
      const ks = ['revenue', 'cogs', 'marketing', 'rndSpend', 'freight', 'storeOps', 'holding', 'overhead', 'interest', 'tax', 'profit'];
      const o: any = {}; ks.forEach((k) => (o[k] = recs.reduce((a, r) => a + (r[k] || 0), 0)));
      o.storeCount = recs[recs.length - 1].storeCount || 0;
      return o;
    };
    let mode = isMode, cur: any, prev: any, curLbl: string, prevLbl: string;
    if (mode === 'ytd') {
      cur = agg(H.filter((r) => yearOf(r) === curYear && r.round <= last.round));
      prev = agg(H.filter((r) => yearOf(r) === curYear - 1 && qiOf(r) <= curQi));
      curLbl = 2026 + curYear + ' YTD'; prevLbl = 2026 + curYear - 1 + ' YTD';
    } else if (mode === 'ly') {
      cur = agg(H.filter((r) => yearOf(r) === curYear - 1));
      prev = agg(H.filter((r) => yearOf(r) === curYear - 2));
      curLbl = String(2026 + curYear - 1); prevLbl = String(2026 + curYear - 2);
    } else {
      mode = 'q'; cur = agg([last]);
      const pq = H[H.length - 2]; prev = pq ? agg([pq]) : null;
      curLbl = last.label; prevLbl = pq ? pq.label : '—';
    }
    const spec: [string, string, number, number][] = cur ? [
      ['Revenue (net of distribution fees)', 'revenue', 1, 1],
      ['Cost of goods sold', 'cogs', -1, 0],
      ['Marketing', 'marketing', -1, 0],
      ['R&D', 'rndSpend', -1, 0],
      ['Logistics & freight', 'freight', -1, 0],
      ['Retail operations (' + (cur.storeCount || 0) + ' stores)', 'storeOps', -1, 0],
      ['Inventory holding', 'holding', -1, 0],
      ['Overhead', 'overhead', -1, 0],
      ['Interest', 'interest', -1, 0],
      ['Tax', 'tax', -1, 0],
      ['Net profit', 'profit', 1, 1],
    ] : [];
    const pills: ['q' | 'ytd' | 'ly', string][] = [['q', 'Last quarter'], ['ytd', 'YTD'], ['ly', 'Last year']];
    return (
      <div className="card">
        <div className="spread" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3>Income statement — {curLbl}</h3>
          <div className="opt-row">{pills.map(([id, l]) => <button key={id} className={'opt-pill' + (isMode === id ? ' on' : '')} onClick={() => setIsMode(id)}>{l}</button>)}</div>
        </div>
        {!cur ? <div className="sub" style={{ marginTop: 8 }}>No data for this period yet.</div> : (
          <>
            <table className="tbl">
              <thead><tr><th>Line item</th><th className="num">{curLbl}</th><th className="num">{prevLbl}</th><th className="num">Trend</th></tr></thead>
              <tbody>
                {spec.map(([l, key, sign], i) => {
                  const isTot = i === spec.length - 1, isRev = i === 0;
                  const v = sign * cur[key], pv = prev ? sign * prev[key] : null;
                  const vc = v >= 0 ? (isRev || isTot ? 'pos' : '') : 'neg';
                  return (
                    <tr key={key} style={isTot ? { fontWeight: 800, borderTop: '2px solid var(--card-b)' } : undefined}>
                      <td>{l}</td><td className={'num ' + vc}>{fmtM(v)}</td><td className="num sub">{pv == null ? '—' : fmtM(pv)}</td><td className="num"><Trend cur={v} prev={pv} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="sub" style={{ marginTop: 10 }}>Cash: <b>{fmtM(last.cash)}</b>{(last.totDebt != null ? last.totDebt : last.loan) > 0 ? <> · Debt: <b className="neg">{fmtM(last.totDebt != null ? last.totDebt : last.loan)}</b></> : null} · Retained earnings: <b className={last.cumProfit >= 0 ? 'pos' : 'neg'}>{fmtM(last.cumProfit)}</b>{last.divPaidCum > 0 ? <> · Dividends paid (cum.): <b>{fmtM(last.divPaidCum)}</b></> : null}</div>
          </>
        )}
      </div>
    );
  };

  // ── scorecard radar ────────────────────────────────────────────────────────────
  const radar = () => {
    const data = Object.keys(SCL).map((k) => ({ metric: SCL[k], value: Math.round((last.sc?.[k] ?? 0)) }));
    return (
      <div className="card">
        <h3>Balanced scorecard — {last.label}</h3>
        <ResponsiveContainer width="100%" height={230}>
          <RadarChart data={data} outerRadius="72%">
            <PolarGrid stroke="var(--card-b)" />
            <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--mut)', fontSize: 10 }} />
            <Radar dataKey="value" stroke="var(--acc)" fill="var(--acc)" fillOpacity={0.18} isAnimationActive={!reduceMotion} />
            <Tooltip contentStyle={TT} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // ── balance sheet ───────────────────────────────────────────────────────────────
  const balanceSection = () => {
    const startCash = g.startCash || 20e6;
    const snap = (r: any): any => {
      if (!r) return null;
      const cash = r.cash || 0;
      const inv = (r.products || []).reduce((a: number, p: any) => a + (p.inventory || 0) * (p.uc || 0), 0);
      const stDebt = r.loan || 0, ltDebt = r.termDebt != null ? r.termDebt : 0, totDebt = stDebt + ltDebt;
      const paidIn = r.paidIn != null ? r.paidIn : startCash, retained = r.cumProfit || 0;
      const equity = paidIn + retained, totAssets = equity + totDebt;
      const nonCur = Math.max(0, totAssets - cash - inv);
      let factories = Math.max(0, r.factoriesVal || 0), warehouses = Math.max(0, r.warehousesVal || 0);
      if (factories + warehouses > nonCur && factories + warehouses > 0) { const sc = nonCur / (factories + warehouses); factories *= sc; warehouses *= sc; }
      const retailDist = Math.max(0, nonCur - factories - warehouses);
      const storesVal = Math.min(retailDist, (r.storeCount || 0) * 3.5e5), distEntry = Math.max(0, retailDist - storesVal);
      return { cash, inv, curAssets: cash + inv, factories, warehouses, retailDist, storesVal, distEntry, nonCur, totAssets, stDebt, ltDebt, totDebt, paidIn, retained, equity, totLE: totDebt + equity };
    };
    const hasYear = H.length >= 5;
    let mode = bsMode; if (mode === 'y' && !hasYear) mode = 'q';
    const prev = mode === 'y' ? H[H.length - 5] : H[H.length - 2];
    const c = snap(last), p = snap(prev);
    const curLbl = last.label, prevLbl = prev ? prev.label : '—';
    const de = last.leverage != null ? last.leverage : c.totDebt / Math.max(1, c.equity);
    const rating = last.rating || Eng.creditRating(g, H);
    const oPct = last.ownerPct != null ? last.ownerPct : Eng.ownerPct(g);
    const band = Eng.controlBand(oPct);
    const curRatio = c.totDebt > 0 ? c.curAssets / c.totDebt : null;
    // Render functions, not inline components: a component defined inside the
    // screen gets a new identity each render and remounts the whole table.
    const row = (l: any, k: string, o: { ind?: boolean; tot?: boolean; grand?: boolean } = {}) => (
      <tr key={k} style={o.tot ? { fontWeight: 800, borderTop: (o.grand ? '2px' : '1px') + ' solid var(--card-b)' } : undefined}>
        <td style={o.ind ? { paddingLeft: 22 } : undefined}>{l}</td>
        <td className="num"><b>{fmtM(c[k])}</b></td>
        <td className="num sub">{p ? fmtM(p[k]) : '—'}</td>
        <td className="num">{p ? <Trend cur={c[k]} prev={p[k]} /> : <span className="sub">—</span>}</td>
      </tr>
    );
    const hdr = (t: string) => <tr key={'h:' + t}><td colSpan={4} style={{ paddingTop: 12 }}><b style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--acc2)' }}>{t}</b></td></tr>;
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3>Balance sheet — {curLbl}</h3>
          <div className="opt-row"><button className={'opt-pill' + (mode === 'q' ? ' on' : '')} onClick={() => setBsMode('q')}>Last quarter</button>{hasYear ? <button className={'opt-pill' + (mode === 'y' ? ' on' : '')} onClick={() => setBsMode('y')}>Last year</button> : null}</div>
        </div>
        <table className="tbl">
          <thead><tr><th>Line item</th><th className="num">{curLbl}</th><th className="num">{prevLbl}</th><th className="num">Trend</th></tr></thead>
          <tbody>
            {hdr('Assets')}
            {row('Cash & equivalents', 'cash', { ind: true })}{row('Inventory (at cost)', 'inv', { ind: true })}{row('Total current assets', 'curAssets', { tot: true })}
            {row('Factories', 'factories', { ind: true })}{row('Warehouses', 'warehouses', { ind: true })}
            {row(<span title={'Owned retail stores: ' + fmtM(c.storesVal) + ' · Distributor & carrier entry: ' + fmtM(c.distEntry)} style={{ borderBottom: '1px dotted var(--dim)', cursor: 'help' }}>Retail & distribution</span>, 'retailDist', { ind: true })}
            {row('Total non-current assets', 'nonCur', { tot: true })}{row('TOTAL ASSETS', 'totAssets', { tot: true, grand: true })}
            {hdr('Liabilities')}
            {row('Credit line / short-term debt', 'stDebt', { ind: true })}{row('Long-term debt (term loans & bonds)', 'ltDebt', { ind: true })}{row('Total liabilities', 'totDebt', { tot: true })}
            {hdr('Owner’s equity')}
            {row('Paid-in capital', 'paidIn', { ind: true })}{row('Retained earnings', 'retained', { ind: true })}{row('Total equity', 'equity', { tot: true })}{row('TOTAL LIABILITIES & EQUITY', 'totLE', { tot: true, grand: true })}
          </tbody>
        </table>
        <div className="sub" style={{ marginTop: 10 }}>
          <span className="chip">Assets = Liabilities + Equity</span> &nbsp; Book equity <b>{fmtM(c.equity)}</b>{curRatio != null ? <> · Current ratio <b>{curRatio.toFixed(1)}×</b></> : null} · Equity funds <b>{Math.round(c.equity / Math.max(1, c.totAssets) * 100)}%</b> of assets
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <span className="chip">D/E {de.toFixed(2)}×</span><span className="chip">Credit rating {rating}</span><span className="chip">{fmtK(last.shares || g.shares)} shares</span>
            <span className={'chip ' + (band.key === 'majority' ? 'acc' : '')}>Founder ownership {Math.round(oPct * 100)}% · {band.label}</span>
          </div>
        </div>
      </div>
    );
  };

  // ── KPI table ───────────────────────────────────────────────────────────────────
  const kpiSection = () => {
    const i = H.length - 1, cur = kpiStats(g, i), pv = i > 0 ? kpiStats(g, i - 1) : null;
    const P = (v: any) => (v == null ? '—' : (v * 100).toFixed(1) + '%');
    const X = (v: any) => (v == null ? '—' : v.toFixed(1) + '×');
    const M = (v: any) => (v == null ? '—' : fmtM(v));
    const D2 = (v: any) => (v == null ? '—' : (v < 0 ? '-' : '') + '$' + Math.abs(v).toFixed(2));
    const $f = (v: any) => (v == null ? '—' : fmt$(v));
    const N = (v: any) => (v == null ? '—' : String(Math.round(v)));
    type R = ['hdr', string] | [string, string, (v: any) => string, string?];
    const rows: R[] = [
      ['hdr', 'Profitability & returns'],
      ['Gross margin', 'gross', P, '(revenue − COGS) ÷ revenue'],
      ['Operating margin', 'op', P, 'profit before interest & tax ÷ revenue'],
      ['Net profit margin (ROS)', 'net', P, 'net profit ÷ revenue'],
      ['Return on equity', 'roe', P, 'trailing 12-mo profit ÷ book equity'],
      ['Earnings per share (ttm)', 'eps', D2, 'trailing 12-mo profit ÷ ' + fmtK(g.shares || 2e6) + ' shares'],
      ['hdr', 'Growth'],
      ['Revenue growth (QoQ)', 'revG', P, 'vs. last quarter'],
      ['Revenue growth (YoY)', 'revGY', P, 'vs. the same quarter last year'],
      ['hdr', 'Efficiency & operations'],
      ['Average selling price', 'asp', $f, 'revenue ÷ units sold'],
      ['Average unit cost', 'ucost', $f, 'COGS ÷ units sold'],
      ['Contribution margin', 'contrib', P, 'after COGS and freight'],
      ['Inventory turns (annualized)', 'turns', X, 'COGS ÷ inventory value — 3×+ is healthy'],
      ['Marketing / revenue', 'mktPct', P],
      ['R&D / revenue', 'rndPct', P],
      ['Stockout rate', 'stockout', P, "demand you couldn't fill from stock"],
      ['hdr', 'Financial position'],
      ['Cash', 'cash', M], ['Debt', 'debt', M, 'credit line at 2%/qtr interest'],
      ['Owner’s equity (book)', 'equity', M, 'paid-in capital + retained earnings'],
      ['Debt-to-equity', 'd2e', X],
      ['hdr', 'Market & customer'],
      ['Market share', 'share', P, 'of industry units sold'],
      ['Brand loyalty', 'brand', N, '0–100'],
      ['Customer satisfaction', 'sat', N, '0–100'],
    ];
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <h3>Key performance indicators — {last.label}</h3>
        <table className="tbl">
          <thead><tr><th>Indicator</th><th className="num">This qtr</th><th className="num">Last qtr</th><th className="num">Trend</th></tr></thead>
          <tbody>
            {rows.map((rw, idx) => {
              if (rw[0] === 'hdr') return <tr key={idx}><td colSpan={4} style={{ paddingTop: 12 }}><b style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--acc2)' }}>{rw[1]}</b></td></tr>;
              const [label, key, fmt, note] = rw as [string, string, (v: any) => string, string?];
              return (
                <tr key={idx}>
                  <td>{label}{note ? <div className="sub" style={{ fontSize: 10.5 }}>{note}</div> : null}</td>
                  <td className="num"><b>{fmt(cur[key])}</b></td>
                  <td className="num sub">{pv ? fmt(pv[key]) : '—'}</td>
                  <td className="num"><Trend cur={cur[key]} prev={pv ? pv[key] : null} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── stock performance ────────────────────────────────────────────────────────────
  const stockSection = () => {
    const prev = H[H.length - 2];
    const price = last.stockPrice, shares = g.shares || 2e6;
    const ttm = Eng.earnTTM(H, H.length - 1), eps = ttm / shares;
    const qoq = prev ? price / prev.stockPrice - 1 : price / 10 - 1, ret = price / 10 - 1;
    const oPct = last.ownerPct != null ? last.ownerPct : Eng.ownerPct(g), band = Eng.controlBand(oPct);
    const pctf = (v: number) => (v >= 0 ? '+' : '') + (v * 100).toFixed(1) + '%';
    const tile = (l: string, v: any, sub?: any, cls?: string) => <div className="card" style={{ padding: '12px 14px' }}><div className="sub">{l}</div><div className={'big-num ' + (cls || '')} style={{ fontSize: 21 }}>{v}</div>{sub ? <div className="sub">{sub}</div> : null}</div>;
    const data = H.map((h) => ({ label: h.label, Price: Number((h.stockPrice ?? 0).toFixed(2)) }));
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread"><h3>Stock performance</h3><span className="chip acc">📈 {fmtK(shares)} shares · IPO $10.00</span></div>
        <div className="grid3" style={{ gridTemplateColumns: 'repeat(5,1fr)', margin: '10px 0 12px' }}>
          {tile('Share price', '$' + price.toFixed(2), null, qoq >= 0 ? 'pos' : 'neg')}
          {tile('Change (QoQ)', pctf(qoq), null, qoq >= 0 ? 'pos' : 'neg')}
          {tile('Market cap', fmtM(price * shares))}
          {tile('EPS (ttm)', (eps < 0 ? '-' : '') + '$' + Math.abs(eps).toFixed(2), eps > 0 ? 'P/E ' + (price / eps).toFixed(1) + '×' : 'P/E —')}
          {tile('Return since IPO', pctf(ret), null, ret >= 0 ? 'pos' : 'neg')}
        </div>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart data={data} margin={{ left: 0, right: 8, top: 4 }}>
            <CartesianGrid stroke="rgb(var(--c-txt) / .08)" vertical={false} /><XAxis dataKey="label" stroke="var(--mut)" fontSize={11} /><YAxis stroke="var(--mut)" fontSize={11} width={44} /><Tooltip contentStyle={TT} />
            <Line type="monotone" dataKey="Price" stroke="var(--warn)" strokeWidth={2} dot={false} isAnimationActive={!reduceMotion} />
          </LineChart>
        </ResponsiveContainer>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <span className={'chip ' + (band.key === 'majority' ? 'acc' : '')}>👑 You own {Math.round(oPct * 100)}% · {band.label}</span>
          {last.controlPenalty > 0 ? <span className="chip" style={{ color: 'var(--bad)' }}>control penalty −{Math.round(last.controlPenalty * 100)}%</span> : null}
          {g.divRatio > 0 ? <span className="chip">dividend {Math.round(g.divRatio * 100)}% payout</span> : null}
          {g.divPaidCum > 0 ? <span className="chip">paid {fmtM(g.divPaidCum)} total · you pocketed {fmtM(g.ownerWealth)}</span> : null}
        </div>
      </div>
    );
  };

  // ── product performance ──────────────────────────────────────────────────────────
  const productTable = () => {
    const groups: Record<string, any[]> = {}; const order: string[] = [];
    for (const p of last.products) { const k = p.mid || p.id; if (!groups[k]) { groups[k] = []; order.push(k); } groups[k].push(p); }
    const row = (p: any, indent: boolean) => (
      <tr key={p.id + '-' + (p.gen ?? 0)}>
        <td style={indent ? { paddingLeft: 26 } : undefined}>{indent ? <GenChip status={p.status || 'current'} label={'Gen ' + p.gen} /> : <b>{p.name}</b>}</td>
        <td><SegTag segId={p.seg} /></td><td className="num">{fmt$(p.price)}</td><td className="num">{fmt$(p.uc)}</td><td className="num">{fmtK(p.demand)}</td><td className="num">{fmtK(p.sold)}</td>
        <td className={'num ' + (p.stockout > 0.05 ? 'neg' : '')}>{Math.round(p.stockout * 100)}%</td><td className="num">{fmtK(p.inventory)}</td><td className="num">{fmtM(p.revenue)}</td>
      </tr>
    );
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <h3>Product performance — {last.label}</h3>
        <table className="tbl">
          <thead><tr><th>Product</th><th>Segment</th><th className="num">Price</th><th className="num">Unit cost</th><th className="num">Demand</th><th className="num">Sold</th><th className="num">Stockout</th><th className="num">Inventory</th><th className="num">Revenue</th></tr></thead>
          <tbody>
            {order.flatMap((k) => {
              const ps = groups[k];
              if (!ps || ps.length === 0) return [];
              const first = ps[0]!;
              if (ps.length === 1) return [row(first, false)];
              const t = (f: string) => ps.reduce((a, p) => a + p[f], 0);
              return [
                <tr key={'sub-' + k} style={{ fontWeight: 700 }}><td><b>{first.model || first.name}</b> <span className="sub">· {ps.length} gens</span></td><td><SegTag segId={first.seg} /></td><td className="num sub">—</td><td className="num sub">—</td><td className="num">{fmtK(t('demand'))}</td><td className="num">{fmtK(t('sold'))}</td><td className="num sub">—</td><td className="num">{fmtK(t('inventory'))}</td><td className="num">{fmtM(t('revenue'))}</td></tr>,
                ...ps.slice().sort((a, b) => (b.gen || 0) - (a.gen || 0)).map((p) => row(p, true)),
              ];
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── markets by region ────────────────────────────────────────────────────────────
  const marketsByRegion = () => {
    if (!last.byCountry) return null;
    return (
      <>
        <div className="grid3" style={{ marginBottom: 14 }}>
          <div className="card"><div className="sub">World coverage (demand-wtd)</div><div className="big-num">{Math.round((last.coverageAvg || 0) * 100)}%</div></div>
          <div className="card"><div className="sub">Est. sales lost to coverage gaps</div><div className={'big-num ' + (last.unserved > 1e5 ? 'neg' : '')}>{fmtK(last.unserved || 0)}</div><div className="sub">buyers your distribution couldn’t reach</div></div>
          <div className="card"><div className="sub">Marketing control</div><div className="big-num">{Math.round((last.ctrlShare != null ? last.ctrlShare : 1) * 100)}%</div><div className="sub">of sales under your campaigns</div></div>
        </div>
        <div className="card" style={{ marginBottom: 14 }}>
          <h3>Markets — units & revenue by region ({last.label})</h3>
          {REGS.map((r) => {
            const ccs = active.filter((c) => c.region === r.id).map((c) => ({ c, cm: last.byCountry[c.id] })).filter((x) => x.cm).sort((a, b) => b.cm.units - a.cm.units);
            if (!ccs.length) return null;
            const u = ccs.reduce((a, x) => a + x.cm.units, 0), rev = ccs.reduce((a, x) => a + x.cm.revenue, 0), uns = ccs.reduce((a, x) => a + x.cm.unserved, 0);
            return (
              <details key={r.id}>
                <summary><div className="spread" style={{ display: 'inline-flex', width: 'calc(100% - 10px)' }}><b>▸ {r.name}</b><span className="sub" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtK(u)} units · {fmtM(rev)}{uns > 5000 ? <> · <span className="neg">{fmtK(uns)} unserved</span></> : null}</span></div></summary>
                <table className="tbl" style={{ margin: '4px 0 8px' }}><tbody>
                  {ccs.map(({ c, cm }) => <tr key={c.id}><td style={{ paddingLeft: 24 }}><Flag cc={c.id} country={c.name} />{c.name}</td><td className="num">{fmtK(cm.units)}</td><td className="num">{fmtM(cm.revenue)}</td><td className="num">{Math.round(cm.cov * 100)}% cov</td><td className={'num ' + (cm.unserved > 5000 ? 'neg' : '')}>{cm.unserved > 1000 ? fmtK(cm.unserved) + ' unserved' : '—'}</td></tr>)}
                </tbody></table>
              </details>
            );
          })}
        </div>
      </>
    );
  };

  // ── trend charts ──────────────────────────────────────────────────────────────────
  const trendCharts = () => {
    const finData = H.map((h) => ({ label: h.label, Revenue: Number((h.revenue / 1e6).toFixed(2)), Profit: Number((h.profit / 1e6).toFixed(2)) }));
    const scoreData = H.map((h) => ({ label: h.label, Composite: Math.round(h.composite ?? 0) }));
    return (
      <div className="grid2">
        <div className="card">
          <h3>Revenue & profit by quarter ($M)</h3>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={finData} margin={{ left: 0, right: 8, top: 4 }}>
              <CartesianGrid stroke="rgb(var(--c-txt) / .08)" vertical={false} /><XAxis dataKey="label" stroke="var(--mut)" fontSize={11} /><YAxis stroke="var(--mut)" fontSize={11} width={36} /><Tooltip contentStyle={TT} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Revenue" fill="rgb(var(--c-acc) / .6)" isAnimationActive={!reduceMotion} /><Bar dataKey="Profit" fill="rgb(var(--c-good) / .65)" isAnimationActive={!reduceMotion} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3>Scorecard trend</h3>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={scoreData} margin={{ left: 0, right: 8, top: 4 }}>
              <CartesianGrid stroke="rgb(var(--c-txt) / .08)" vertical={false} /><XAxis dataKey="label" stroke="var(--mut)" fontSize={11} /><YAxis stroke="var(--mut)" fontSize={11} width={36} domain={[0, 100]} /><Tooltip contentStyle={TT} />
              <Line type="monotone" dataKey="Composite" stroke="var(--vio)" strokeWidth={2} dot={false} isAnimationActive={!reduceMotion} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="rp-root">
      <div className="screen-head"><h2>📊 Reports &amp; Financials</h2><div className="sub">{last.label} results and full history.</div></div>
      <div className="grid2" style={{ gridTemplateColumns: '1.2fr .8fr', marginBottom: 14 }}>
        {incomeSection()}
        {radar()}
      </div>
      {balanceSection()}
      {kpiSection()}
      {stockSection()}
      {productTable()}
      {marketsByRegion()}
      {trendCharts()}
    </div>
  );
}
